import React from "react";
import {
  Typography,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import produce from "immer";
import { initialState } from "./initialState";

export const glassPrescriptionHeaders = [
  "Glass Prescription",
  "SPH",
  "CYL",
  "Axis",
  "PD",
  "ADD",
  "Prism/VA"
];

interface GlassPrescriptionProps {
  visionData: typeof initialState;
  onVisionChange: (updatedData: typeof initialState) => void;
}

const COLS = [
  { key: "sph", label: "SPH" },
  { key: "cyl", label: "CYL" },
  { key: "axis", label: "Axis" },
  { key: "pd", label: "PD" },
  { key: "add", label: "ADD" },
  { key: "prism", label: "Prism/VA" }
] as const;

const ROWS = ["OD", "OS"];

export default function GlassPrescription({
  visionData,
  onVisionChange
}: GlassPrescriptionProps): React.JSX.Element {
  const handleChange = (row: string, col: string, value: string) => {
    const updated = produce(visionData, (draft: any) => {
      draft.glassPrescription[row][col] = value === "" ? null : value;
    });
    onVisionChange(updated);
  };

  const handleRemarksChange = (value: string) => {
    const updated = produce(visionData, (draft: typeof initialState) => {
      draft.remarks.glassPrescription = value;
    });
    onVisionChange(updated);
  };

  const currentGlassPrescription = visionData?.glassPrescription || {};

  const cellStyle = {
    border: 1,
    borderColor: "divider"
  };

  const headerCellStyle = {
    ...cellStyle,
    fontWeight: 600,
    textAlign: "center" as const
  };

  return (
    <Box>
      <TableContainer>
        <Typography
          sx={{
            padding: 1,
            textAlign: "center",
            backgroundColor: "#ededed",
            borderLeft: 1,
            borderRight: 1,
            borderTop: 1,
            borderColor: "divider"
          }}
        >
          Glass Prescription
        </Typography>

        <Table size="small" sx={{ border: 1, borderColor: "divider", borderRadius: "5px" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellStyle, width: "150px" }} />
              {COLS.map((col) => (
                <TableCell key={col.key} sx={headerCellStyle}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {ROWS.map((row) => (
              <TableRow key={row}>
                <TableCell sx={{ ...headerCellStyle, textAlign: "left" }}>{row}</TableCell>
                {COLS.map((col) => (
                  <TableCell key={col.key} sx={cellStyle}>
                    <TextField
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={(currentGlassPrescription as any)[row]?.[col.key] ?? ""}
                      onChange={(e) => handleChange(row, col.key, e.target.value)}
                      sx={{
                        "& .MuiInputBase-input": {
                          padding: "4px 8px",
                          fontSize: "0.875rem"
                        }
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TextField
        sx={{ mt: 2 }}
        fullWidth
        variant="outlined"
        size="small"
        placeholder="Remarks"
        value={visionData?.remarks?.glassPrescription || ""}
        onChange={(e) => handleRemarksChange(e.target.value)}
      />
    </Box>
  );
}
